import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Package, Plus, Truck, X } from "lucide-react";
import { platformClient, formatPlatformError } from "./platformApi";

const inputCls =
    "mt-2 w-full border-b border-white/20 bg-transparent pb-2.5 text-sm text-bone placeholder:text-ash/50 transition-colors focus:border-gold focus:outline-none";
const labelCls = "text-[10px] uppercase tracking-[0.25em] text-ash";
const euro = (v) => `€ ${Number(v || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const EMPTY_STOCK = { name: "", unit: "stuk", stock: "", min_stock: "", unit_cost: "", supplier: "" };
const EMPTY_PO_ITEM = { name: "", quantity: 1, unit_price: "" };
const EMPTY_PO = { supplier: "", items: [{ ...EMPTY_PO_ITEM }], expected_date: "", notes: "" };

export const StockTab = () => {
    const [stock, setStock] = useState([]);
    const [pos, setPos] = useState([]);
    const [form, setForm] = useState(EMPTY_STOCK);
    const [poForm, setPoForm] = useState(EMPTY_PO);
    const [showStockForm, setShowStockForm] = useState(false);
    const [showPoForm, setShowPoForm] = useState(false);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const [s, p] = await Promise.all([
                platformClient.get("/stock"),
                platformClient.get("/purchase-orders"),
            ]);
            setStock(s.data);
            setPos(p.data);
        } catch {}
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const lowStock = useMemo(() => stock.filter((i) => i.min_stock > 0 && i.stock < i.min_stock), [stock]);

    const createStock = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            await platformClient.post("/stock", {
                ...form,
                stock: Number(form.stock) || 0,
                min_stock: Number(form.min_stock) || 0,
                unit_cost: Number(form.unit_cost) || 0,
            });
            setForm(EMPTY_STOCK);
            setShowStockForm(false);
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const adjust = async (item, delta) => {
        const next = Math.max(0, (item.stock || 0) + delta);
        setStock((list) => list.map((i) => (i.id === item.id ? { ...i, stock: next } : i)));
        try {
            await platformClient.patch(`/stock/${item.id}`, { stock: next });
        } catch {
            load();
        }
    };

    const removeStock = async (item) => {
        if (!window.confirm(`${item.name} uit de voorraad verwijderen?`)) return;
        try {
            await platformClient.delete(`/stock/${item.id}`);
            load();
        } catch {}
    };

    const createPo = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            await platformClient.post("/purchase-orders", {
                ...poForm,
                items: poForm.items
                    .filter((i) => i.name.trim())
                    .map((i) => ({ name: i.name.trim(), quantity: Number(i.quantity) || 1, unit_price: Number(i.unit_price) || 0 })),
            });
            setPoForm(EMPTY_PO);
            setShowPoForm(false);
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const receive = async (po) => {
        try {
            await platformClient.post(`/purchase-orders/${po.id}/receive`);
            load();
        } catch {}
    };

    const setPoItem = (idx, key, val) =>
        setPoForm({ ...poForm, items: poForm.items.map((it, i) => (i === idx ? { ...it, [key]: val } : it)) });

    return (
        <div data-testid="stock-tab" className="mt-8">
            {lowStock.length ? (
                <div data-testid="stock-low-alert" className="mb-6 flex items-center gap-3 border border-amber-400/40 bg-amber-400/5 px-5 py-4">
                    <AlertTriangle className="h-4 w-4 text-amber-300" />
                    <p className="text-sm text-amber-200">
                        Onder minimum: {lowStock.map((i) => i.name).join(", ")} — overweeg een inkooporder.
                    </p>
                </div>
            ) : null}

            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                <div>
                    <div className="flex items-center justify-between">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-gold">Voorraad</p>
                        <button data-testid="stock-add-button" onClick={() => setShowStockForm((v) => !v)}
                            className="flex items-center gap-2 border border-gold/50 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-black">
                            {showStockForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                            {showStockForm ? "Sluiten" : "Materiaal"}
                        </button>
                    </div>

                    {showStockForm ? (
                        <form data-testid="stock-form" onSubmit={createStock} className="mt-4 space-y-4 border border-white/10 bg-coal p-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <label className={labelCls}>Naam *</label>
                                    <input data-testid="stock-form-name" required className={inputCls} value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Bijv. RVS buis 42,4mm" />
                                </div>
                                <div>
                                    <label className={labelCls}>Eenheid</label>
                                    <input data-testid="stock-form-unit" className={inputCls} value={form.unit}
                                        onChange={(e) => setForm({ ...form, unit: e.target.value })} />
                                </div>
                                <div>
                                    <label className={labelCls}>Leverancier</label>
                                    <input data-testid="stock-form-supplier" className={inputCls} value={form.supplier}
                                        onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
                                </div>
                                <div>
                                    <label className={labelCls}>Voorraad</label>
                                    <input data-testid="stock-form-stock" type="number" min="0" step="any" className={inputCls} value={form.stock}
                                        onChange={(e) => setForm({ ...form, stock: e.target.value })} />
                                </div>
                                <div>
                                    <label className={labelCls}>Minimum</label>
                                    <input data-testid="stock-form-min" type="number" min="0" step="any" className={inputCls} value={form.min_stock}
                                        onChange={(e) => setForm({ ...form, min_stock: e.target.value })} />
                                </div>
                                <div>
                                    <label className={labelCls}>Kostprijs p/eenh (€)</label>
                                    <input data-testid="stock-form-cost" type="number" min="0" step="any" className={inputCls} value={form.unit_cost}
                                        onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} />
                                </div>
                            </div>
                            {error ? <p className="text-xs text-red-400">{error}</p> : null}
                            <button data-testid="stock-form-submit" disabled={busy}
                                className="border border-gold bg-gold px-6 py-2.5 text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-transparent hover:text-gold disabled:opacity-50">
                                {busy ? "Bezig…" : "Toevoegen"}
                            </button>
                        </form>
                    ) : null}

                    <div className="mt-4 space-y-2">
                        {stock.length === 0 ? (
                            <p data-testid="stock-empty" className="border border-white/10 bg-coal p-8 text-sm text-ash">Nog geen voorraad.</p>
                        ) : (
                            stock.map((i) => {
                                const low = i.min_stock > 0 && i.stock < i.min_stock;
                                return (
                                    <div key={i.id} data-testid={`stock-item-${i.id}`}
                                        className={`flex items-center gap-3 border bg-coal p-4 ${low ? "border-amber-400/40" : "border-white/10"}`}>
                                        <Package className={`h-4 w-4 shrink-0 ${low ? "text-amber-300" : "text-gold"}`} />
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-sm text-bone">{i.name}</p>
                                            <p className="text-xs text-ash">
                                                {i.supplier || "—"} · min {i.min_stock} {i.unit}
                                                {low ? <span data-testid={`stock-low-${i.id}`} className="text-amber-300"> · te laag</span> : null}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button data-testid={`stock-minus-${i.id}`} onClick={() => adjust(i, -1)}
                                                className="border border-white/15 px-2.5 py-1 text-xs text-ash transition-colors hover:border-gold/50 hover:text-gold">−</button>
                                            <span data-testid={`stock-count-${i.id}`} className={`w-16 text-center text-sm ${low ? "text-amber-300" : "text-bone"}`}>
                                                {i.stock} {i.unit}
                                            </span>
                                            <button data-testid={`stock-plus-${i.id}`} onClick={() => adjust(i, 1)}
                                                className="border border-white/15 px-2.5 py-1 text-xs text-ash transition-colors hover:border-gold/50 hover:text-gold">+</button>
                                            <button data-testid={`stock-delete-${i.id}`} onClick={() => removeStock(i)}
                                                className="text-ash/40 transition-colors hover:text-red-300" aria-label="Verwijderen">
                                                <X className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                <div>
                    <div className="flex items-center justify-between">
                        <p className="text-[11px] uppercase tracking-[0.3em] text-gold">Inkooporders</p>
                        <button data-testid="po-add-button" onClick={() => setShowPoForm((v) => !v)}
                            className="flex items-center gap-2 border border-gold/50 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-black">
                            {showPoForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                            {showPoForm ? "Sluiten" : "Inkooporder"}
                        </button>
                    </div>

                    {showPoForm ? (
                        <form data-testid="po-form" onSubmit={createPo} className="mt-4 space-y-4 border border-white/10 bg-coal p-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelCls}>Leverancier *</label>
                                    <input data-testid="po-form-supplier" required className={inputCls} value={poForm.supplier}
                                        onChange={(e) => setPoForm({ ...poForm, supplier: e.target.value })} placeholder="Bijv. Staaldiscounter.nl" />
                                </div>
                                <div>
                                    <label className={labelCls}>Verwachte levering</label>
                                    <input data-testid="po-form-date" type="date" className={inputCls} value={poForm.expected_date}
                                        onChange={(e) => setPoForm({ ...poForm, expected_date: e.target.value })} />
                                </div>
                            </div>
                            <div>
                                <p className={labelCls}>Regels</p>
                                <div className="mt-3 space-y-3">
                                    {poForm.items.map((item, idx) => (
                                        <div key={idx} className="flex items-end gap-3">
                                            <input data-testid={`po-item-name-${idx}`} className={`${inputCls} flex-1`} value={item.name}
                                                onChange={(e) => setPoItem(idx, "name", e.target.value)} placeholder="Materiaal (zoals in voorraad)" list="po-stock-names" />
                                            <input data-testid={`po-item-qty-${idx}`} type="number" min="0" step="any" className={`${inputCls} w-20`} value={item.quantity}
                                                onChange={(e) => setPoItem(idx, "quantity", e.target.value)} placeholder="Aant." />
                                            <input data-testid={`po-item-price-${idx}`} type="number" min="0" step="any" className={`${inputCls} w-24`} value={item.unit_price}
                                                onChange={(e) => setPoItem(idx, "unit_price", e.target.value)} placeholder="€ p/st" />
                                        </div>
                                    ))}
                                </div>
                                <datalist id="po-stock-names">
                                    {stock.map((s) => (
                                        <option key={s.id} value={s.name} />
                                    ))}
                                </datalist>
                                <button type="button" data-testid="po-item-add"
                                    onClick={() => setPoForm({ ...poForm, items: [...poForm.items, { ...EMPTY_PO_ITEM }] })}
                                    className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-gold transition-colors hover:text-bone">
                                    <Plus className="h-3.5 w-3.5" /> Regel toevoegen
                                </button>
                            </div>
                            {error ? <p className="text-xs text-red-400">{error}</p> : null}
                            <button data-testid="po-form-submit" disabled={busy}
                                className="border border-gold bg-gold px-6 py-2.5 text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-transparent hover:text-gold disabled:opacity-50">
                                {busy ? "Bezig…" : "Bestellen"}
                            </button>
                        </form>
                    ) : null}

                    <div className="mt-4 space-y-2">
                        {pos.length === 0 ? (
                            <p data-testid="po-empty" className="border border-white/10 bg-coal p-8 text-sm text-ash">Nog geen inkooporders.</p>
                        ) : (
                            pos.map((p) => (
                                <div key={p.id} data-testid={`po-${p.id}`} className="border border-white/10 bg-coal p-4">
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="flex items-center gap-2 text-sm text-bone">
                                                <Truck className="h-4 w-4 shrink-0 text-gold" />
                                                <span className="text-gold">{p.number}</span> · {p.supplier}
                                            </p>
                                            <p className="mt-1 text-xs text-ash">
                                                {(p.items || []).map((i) => `${i.quantity}× ${i.name}`).join(", ")}
                                                {p.expected_date ? ` · verwacht ${p.expected_date}` : ""}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="text-sm text-gold">{euro(p.total)}</span>
                                            {p.status === "besteld" ? (
                                                <button data-testid={`po-receive-${p.id}`} onClick={() => receive(p)}
                                                    className="border border-gold/50 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black">
                                                    Ontvangen
                                                </button>
                                            ) : (
                                                <span className="border border-gold/40 bg-gold/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] text-gold">
                                                    Ontvangen
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
